import React from 'react'
import { BForm } from '@prague-digi/ui-components'
import { compose } from 'recompose'
import { connect } from 'react-redux'

import { API_ERROR, OTHER_COST_OF_LIVING_MODAL } from '../../constants/modals'
import { ApiErrorInfo, Response } from '../../api/api'
import {
  BCombobox,
  BMoneyInput,
  PageControlButtons,
  PrefillFormButton,
  Title,
} from '../../components/'
import { clientActions, modalDialogActions } from '../../redux'
import {
  errorHandler,
  form,
  FormInterface,
  intl,
  IntlInterface,
  wizard,
  WizardInterface,
} from '../../hoc'
import { OTHER_COST_OF_LIVING_LIMIT } from '../../constants'
import { ReduxState } from '../../types'
import messages from '../../i18n'
import validate from './FinanceExpenses.validate'

interface Props extends FormInterface, IntlInterface, WizardInterface {
  isModal?: boolean
  onClose: any
  onContinue: any
  saveFinanceExpenses: any
  showModalDialog: any
  storeData: any
}

const FORM_NAME = 'FinanceExpenses'

export class FinanceExpenses extends React.Component<Props> {
  constructor(props: Props) {
    super(props)
    props.initialize({ ...props.storeData })
  }

  onSubmit = () => {
    this.props.handleSubmit(this.saveFinanceExpensesCallback)()
  }

  onBack = () => {
    if (this.props.onClose) {
      this.props.onClose()
    } else {
      this.props.wizard.gotoPrevious()
    }
  }

  checkOtherCostOfLiving = () => {
    if (this.props.values.otherCostOfLiving > OTHER_COST_OF_LIVING_LIMIT) {
      this.props.showModalDialog(
        OTHER_COST_OF_LIVING_MODAL({
          amount: this.props.formatCzechCurrency(this.props.values.otherCostOfLiving),
          callback: this.onSubmit,
        })
      )
    } else {
      this.onSubmit()
    }
  }

  saveFinanceExpensesCallback = () => {
    this.props.saveFinanceExpenses({
      success: () => {
        if (this.props.onContinue) {
          this.props.onContinue()
        } else {
          this.props.wizard.gotoNext()
        }
      },
      error: (errorInfo: ApiErrorInfo) => {
        if (errorInfo.type === Response.dataError && errorInfo.errors) {
          this.props.setErrors(FORM_NAME, errorInfo.errors)
        } else {
          this.props.showModalDialog(API_ERROR({ errorInfo, onRetry: this.onSubmit }))
        }
      },
    })
  }

  dependentsOptions = [
    ...Array.from(Array(9).keys()).map((persons: number) => ({
      value: persons,
      label: this.props.translate(messages.financeExpenses.countPersons, {
        persons,
      }) as string,
    })),
    {
      value: 9,
      label: this.props.translate(messages.financeExpenses.morePersons) as string,
    },
  ]

  render() {
    const { isModal, invalid, translate, translateHTML } = this.props

    return (
      <BForm id={FORM_NAME}>
        <Title center={isModal} message={messages.financeExpenses.formTitle} subTitle={!isModal} />

        <BMoneyInput
          messages={{
            label: translate(messages.financeExpenses.installmentsOutsideMoneta),
            notice: translateHTML(messages.financeExpenses.installmentsOutsideMonetaNote),
          }}
          name="otherMonthlyInstallments"
        />

        <BMoneyInput
          messages={{
            label: translate(messages.financeExpenses.housingExpenditure),
            notice: translateHTML(messages.financeExpenses.housingExpenditureNote),
          }}
          name="housingExpenditure"
        />

        <BMoneyInput
          messages={{
            label: translate(messages.financeExpenses.otherCostOfLiving),
            notice: translateHTML(messages.financeExpenses.otherCostOfLivingNote),
          }}
          name="otherCostOfLiving"
        />

        <BCombobox
          name="numberOfDependents"
          data={this.dependentsOptions}
          messages={{
            label: translate(messages.financeExpenses.numberOfDependents),
            notice: translate(messages.financeExpenses.numberOfDependentsNote),
            prefix: translate(messages.financeExpenses.mePlus),
          }}
          testid="numberOfDependents"
        />

        <PageControlButtons
          disableSubmit={invalid}
          isModal={isModal}
          onSubmit={this.checkOtherCostOfLiving}
          secondaryAction={this.onBack}
        />

        <PrefillFormButton formName="FinanceExpenses" />
      </BForm>
    )
  }
}

export default compose(
  errorHandler('containers/FinanceExpenses/FinanceExpenses.tsx'),
  wizard,
  connect(
    (state: ReduxState) => ({
      storeData: {
        housingExpenditure: state.client.housingExpenditure,
        numberOfDependents: state.client.numberOfDependents,
        otherCostOfLiving: state.client.otherCostOfLiving,
        otherMonthlyInstallments: state.client.otherMonthlyInstallments,
      },
    }),
    {
      saveFinanceExpenses: clientActions.saveFinanceExpenses,
      showModalDialog: modalDialogActions.showModalDialog,
    }
  ),
  form({
    name: FORM_NAME,
    validate,
  }),
  intl
)(FinanceExpenses)
